import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { getFullName } from "../helpers/form.handler";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";

interface AssignmentSectionProps {
  title: string;
  icon: string;
  colorClass: string;
  ids: number[];
  dataSource: any[];
  keyField: string;
  nameField: string;
  type: "departments" | "designations" | "employees";
}

// Custom Modal Component
const ShowMoreModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: any[];
  iconClass: string;
  type: string;
}> = ({ isOpen, onClose, title, items, iconClass, type }) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal-overlay modal-dialog modal-dialog-centered modal-md"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={onClose}>
      <div
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "0",
          width: "50%",
          maxWidth: "1000px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}>
        <div
          className="modal-header"
          style={{ padding: "1rem", borderBottom: "1px solid #e9ecef" }}>
          <h4 className="modal-title" style={{ margin: 0 }}>
            {title}
          </h4>
          <button
            type="button"
            className="btn-close"
            style={{
              position: "absolute",
              right: "1rem",
              top: "1rem",
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
            }}
            onClick={onClose}>
            ×
          </button>
        </div>

        <div
          className="modal-body"
          style={{ padding: "1rem", overflowY: "auto", maxHeight: "60vh" }}>
          <div className="row g-2">
            {items.map((item, index) => (
              <div key={item?.id} className="col-md-6">
                <div className="d-flex align-items-center p-2 bg-light rounded">
                  {type == "employees" ? (
                    <li className="avatar avatar-xl avatar-rounded object-cover flex-shrink-0">
                      <ImageWithBasePath
                        src={
                          !item?.image_url || item?.image_url === "null"
                            ? "assets/img/users/user.png"
                            : item?.image_url
                        }
                        alt="img"
                      />
                    </li>
                  ) : (
                    <i className={`ti ${iconClass} text-primary me-2`}></i>
                  )}
                  <span>{item.name}</span>
                </div>
              </div>
            ))}
          </div>
          {items.length === 0 && (
            <div className="text-center text-muted py-3">
              <i className={`ti ${iconClass} fs-32 mb-2`}></i>
              <p>No {type} assigned</p>
            </div>
          )}
        </div>

        <div
          className="modal-footer"
          style={{ padding: "1rem", borderTop: "1px solid #e9ecef" }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AssignmentSection: React.FC<AssignmentSectionProps> = ({
  title,
  icon,
  colorClass,
  ids,
  dataSource,
  keyField,
  nameField,
  type,
}) => {
  // Modal state - isolated for this component instance
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Memoize the names computation to avoid unnecessary recalculations
  const names = useMemo(() => {
    return ids.map((id) => {
      const item = dataSource.find((d) => d?.id == id || d?.key == id);
      if (type == "departments") {
        return {
          id: item?.key || -1,
          name: item?.Department || "Department N/F",
        };
      } else if (type == "employees") {
        return {
          id: item?.id || -1,
          name:
            getFullName(item?.salutation, item?.fname, item?.lname) ||
            "User N/F",
          image_url: item?.profile_pic || null,
        };
      } else if (type == "designations") {
        return {
          id: item?.key || -1,
          name: item?.Designation || "Designation N/F",
        };
      } else {
        return {
          id: -1,
          name: "N/F",
        };
      }
    });
  }, [ids, dataSource, keyField, nameField, title]);

  // Memoize the icon to avoid unnecessary recalculations
  const iconClass = useMemo(() => {
    switch (type) {
      case "departments":
        return "ti-building-store";
      case "designations":
        return "ti-award";
      case "employees":
        return "ti-users";
      default:
        return "ti-list";
    }
  }, [type]);

  const showMaxItems = type == "employees" ? 2 : 3;

  const displayNames = names.slice(0, showMaxItems);
  const hasMore = names.length > showMaxItems;
  const modalDisplayNames = names.slice(showMaxItems);

  // Use useCallback to prevent function recreation on every render
  const handleShowMore = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <div className="col-md-4">
        <div className="assignment-section">
          <div className="badge badge-secondary-transparent d-flex align-items-center mb-2">
            <i className={`ti ${icon} text-secondary me-2 fs-18`}></i>
            <h6 className="text-dark mb-0">{title}</h6>
          </div>

          <div className="assignment-list">
            {names.length === 0 ? (
              <small className="text-primary px-2">No records to show!</small>
            ) : (
              <>
                {displayNames.map((item, index) => (
                  <div
                    key={item?.id}
                    className="px-1 assignment-item mb-1 d-flex align-items-center">
                    {type == "employees" && (
                      <li className="avatar avatar-md avatar-rounded object-cover flex-shrink-0">
                        <ImageWithBasePath
                          src={
                            !item?.image_url || item?.image_url === "null"
                              ? "assets/img/users/user.png"
                              : item?.image_url
                          }
                          alt="img"
                        />
                      </li>
                    )}
                    <span className={`text-muted me-1`}>{item?.name}</span>
                  </div>
                ))}
                {hasMore && (
                  <Link
                    to="#"
                    className="px-2 btn-link text-decoration-none"
                    onClick={handleShowMore}>
                    <small className="text-primary">
                      +{names.length - showMaxItems} more
                    </small>
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Modal - Only renders when needed */}
      <ShowMoreModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={title}
        items={modalDisplayNames}
        iconClass={iconClass}
        type={type}
      />
    </>
  );
};

export default AssignmentSection;
